import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, CheckCircle2, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="mb-8 text-center">
          <img src={LOGO_URL} alt="Kinsy" className="h-10 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight mb-2">
            Forgot your password?
          </h1>
          <p className="text-stone-500">
            Enter your email and we'll send you a reset link.
          </p>
        </div>

        <div className="card-soft">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-lg font-semibold text-stone-900 mb-2">Check your inbox</h2>
              <p className="text-stone-600 text-sm mb-6">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                The link expires in 60 minutes.
              </p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-stone-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                  className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}
        </div>

        <Link to="/login" className="mt-6 flex items-center justify-center gap-1 text-sm text-stone-500 hover:text-stone-700">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
